import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingBag, Search, User, ChevronDown, Instagram, CreditCard } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCartStore } from '@/stores/cartStore';
import { useCurrencyStore } from '@/stores/currencyStore';
import CartDrawer from './CartDrawer';
import SearchModal from './SearchModal';
import type { Currency } from '@/types';

function TikTokIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
    </svg>
  );
}

const navLinks = [
  { label: 'Products', href: '/products' },
  { label: 'Protocols', href: '/products?category=bundles' },
  { label: 'Quiz', href: '/quiz' },
  { label: 'FAQ', href: '/faq' },
  { label: 'Contact', href: '/contact' },
  { label: 'Founder', href: '/founder' },
];

const currencies: Currency[] = ['AED', 'THB'];

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const cartTotal = useCartStore((s) => s.totalItems());
  const toggleCart = useCartStore((s) => s.toggleCart);
  const { currency, setCurrency } = useCurrencyStore();
  const location = useLocation();

  return (
    <div className="flex min-h-full flex-col">
      {/* Announcement bar */}
      <div className="relative z-[60] overflow-hidden bg-rp-forest py-2 text-rp-paper">
        <div className="rp-container flex items-center justify-between gap-4">
          <div className="relative flex-1 overflow-hidden">
            <motion.div
              className="flex gap-8 whitespace-nowrap font-mono text-[10px] font-bold uppercase tracking-[0.08em]"
              animate={{ x: ['0%', '-50%'] }}
              transition={{ repeat: Infinity, duration: 30, ease: 'linear' }}
            >
              {[...Array(2)].map((_, dup) => (
                <div key={dup} className="flex gap-8">
                  <span>Free worldwide shipping</span>
                  <span>HPLC-verified</span>
                  <span>Cold-chain dispatch</span>
                  <span>Ships next business day</span>
                  <span>ISO-certified manufacturing</span>
                </div>
              ))}
            </motion.div>
          </div>
          <button
            type="button"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-rp-paper bg-rp-paper px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.06em] text-rp-forest"
          >
            <span className="text-[11px]">10%</span>
            <span className="hidden sm:inline">off · first order</span>
            <span className="sm:hidden">off · 1st</span>
            <span>→</span>
          </button>
        </div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-rp-line bg-rp-paper/92 backdrop-blur-md">
        <div className="rp-container">
          <div className="flex h-16 items-center justify-between gap-4">
            {/* Mobile menu */}
            <button
              aria-label="Open menu"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-rp-line text-rp-forest md:hidden"
              onClick={() => setMobileOpen(true)}
            >
              <Menu size={18} />
            </button>

            {/* Currency (mobile) */}
            <div className="relative md:hidden">
              <button
                className="flex h-11 w-11 items-center justify-center rounded-full border border-rp-line text-[10px] font-bold text-rp-forest"
                onClick={() => setCurrencyOpen((v) => !v)}
              >
                {currency}
              </button>
              <AnimatePresence>
                {currencyOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 4 }}
                    className="absolute left-0 top-full mt-2 min-w-[80px] rounded-xl border border-rp-line bg-rp-paper p-1 shadow-dropdown"
                  >
                    {currencies.map((c) => (
                      <button
                        key={c}
                        onClick={() => {
                          setCurrency(c);
                          setCurrencyOpen(false);
                        }}
                        className={`block w-full rounded-lg px-3 py-2 text-left text-xs font-semibold ${currency === c ? 'bg-rp-bone text-rp-forest' : 'text-rp-moss hover:bg-rp-bone'}`}
                      >
                        {c}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 text-xl font-bold tracking-tight text-rp-forest">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-rp-forest text-rp-paper">AU</span>
              <span className="hidden sm:inline">AURA</span>
            </Link>

            {/* Desktop nav */}
            <nav className="hidden items-center gap-1 md:flex">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${location.pathname === link.href.split('?')[0] ? 'bg-rp-bone text-rp-forest' : 'text-rp-moss hover:bg-rp-bone hover:text-rp-forest'}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Right actions */}
            <div className="flex items-center gap-2">
              {/* Currency desktop */}
              <div className="relative hidden md:block">
                <button
                  onClick={() => setCurrencyOpen((v) => !v)}
                  className="flex items-center gap-1 rounded-full border border-rp-line px-3 py-2 text-xs font-bold text-rp-forest"
                >
                  {currency}
                  <ChevronDown size={12} />
                </button>
                <AnimatePresence>
                  {currencyOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 4 }}
                      className="absolute right-0 top-full mt-2 min-w-[90px] overflow-hidden rounded-xl border border-rp-line bg-rp-paper shadow-dropdown"
                    >
                      {currencies.map((c) => (
                        <button
                          key={c}
                          onClick={() => {
                            setCurrency(c);
                            setCurrencyOpen(false);
                          }}
                          className={`block w-full px-4 py-2 text-left text-xs font-semibold ${currency === c ? 'bg-rp-bone text-rp-forest' : 'text-rp-moss hover:bg-rp-bone'}`}
                        >
                          {c}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <button
                aria-label="Search"
                onClick={() => setSearchOpen(true)}
                className="flex h-10 w-10 items-center justify-center rounded-full text-rp-forest hover:bg-rp-bone"
              >
                <Search size={18} />
              </button>
              <Link to="/contact" aria-label="Account" className="flex h-10 w-10 items-center justify-center rounded-full text-rp-forest hover:bg-rp-bone">
                <User size={18} />
              </Link>
              <button
                aria-label="Cart"
                onClick={toggleCart}
                className="relative flex h-10 w-10 items-center justify-center rounded-full bg-rp-forest text-rp-paper"
              >
                <ShoppingBag size={18} />
                {cartTotal > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-rp-leaf text-[10px] font-bold text-rp-forest">
                    {cartTotal}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[70] bg-rp-ink/40"
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed left-0 top-0 z-[80] flex h-full w-[min(340px,85vw)] flex-col bg-rp-paper p-6"
            >
              <div className="mb-8 flex items-center justify-between">
                <span className="text-xl font-bold text-rp-forest">AURA</span>
                <button onClick={() => setMobileOpen(false)} className="flex h-10 w-10 items-center justify-center rounded-full border border-rp-line text-rp-forest">
                  <X size={18} />
                </button>
              </div>
              <nav className="flex flex-col gap-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setMobileOpen(false)}
                    className="rounded-xl px-4 py-3 text-base font-medium text-rp-ink hover:bg-rp-bone"
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
              <div className="mt-auto border-t border-rp-line pt-6 text-sm text-rp-moss">
                <p className="font-mono text-xs font-bold uppercase">Support</p>
                <p className="mt-1">birouti.husam@gmail.com</p>
                <p>+971 56 562 8081</p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <main className="flex-1">{children}</main>
      <CartDrawer />
      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
      <Footer />
    </div>
  );
}

function Footer() {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleNewsletter = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      setSubscribed(true);
      setEmail('');
    }
  };

  const socialLinks = [
    { icon: Instagram, href: 'https://www.instagram.com/hause_peptide/', label: 'Instagram' },
    { icon: TikTokIcon, href: 'https://www.tiktok.com/@husam.peptide', label: 'TikTok' },
  ];

  return (
    <footer className="bg-rp-forest text-rp-paper">
      <div className="rp-container py-16">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-12">
          {/* Brand + newsletter */}
          <div className="lg:col-span-5">
            <Link to="/" className="flex items-center gap-2 text-2xl font-bold">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-rp-paper text-rp-forest">AU</span>
              AURA
            </Link>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-rp-leaf-soft">
              Reference-grade lyophilized peptides. UAE-sourced with COA, batch traceability and research-use compliance.
            </p>

            <div className="mt-6">
              <p className="font-mono text-[10px] font-bold uppercase tracking-[0.08em] text-rp-leaf">Newsletter</p>
              {subscribed ? (
                <p className="mt-2 text-sm text-rp-leaf-soft">You’re on the list. Welcome to AURA.</p>
              ) : (
                <form onSubmit={handleNewsletter} className="mt-2 flex max-w-sm gap-2">
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    className="flex-1 rounded-lg border border-rp-forest-2 bg-rp-forest-2 px-3 py-2 text-sm text-rp-paper placeholder:text-rp-leaf/60 outline-none focus:border-rp-leaf"
                  />
                  <button
                    type="submit"
                    className="rounded-lg bg-rp-paper px-4 py-2 text-xs font-bold uppercase tracking-wider text-rp-forest transition-colors hover:bg-rp-bone"
                  >
                    Join
                  </button>
                </form>
              )}
            </div>

            <div className="mt-6 flex items-center gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-rp-forest-2 text-rp-leaf transition-colors hover:border-rp-leaf hover:text-rp-paper"
                >
                  <social.icon size={16} />
                </a>
              ))}
            </div>
          </div>

          {/* Shop */}
          <div className="lg:col-span-2">
            <h4 className="font-mono text-xs font-bold uppercase tracking-[0.08em] text-rp-leaf">Shop</h4>
            <ul className="mt-4 space-y-2 text-sm">
              <li><Link to="/products" className="hover:text-rp-leaf">All products</Link></li>
              <li><Link to="/products?category=metabolic" className="hover:text-rp-leaf">Metabolic</Link></li>
              <li><Link to="/products?category=bundles" className="hover:text-rp-leaf">Protocols & Stacks</Link></li>
              <li><Link to="/products?category=tissue-repair" className="hover:text-rp-leaf">New arrivals</Link></li>
              <li><Link to="/quiz" className="hover:text-rp-leaf">Personalized plan</Link></li>
            </ul>
          </div>

          {/* Support */}
          <div className="lg:col-span-2">
            <h4 className="font-mono text-xs font-bold uppercase tracking-[0.08em] text-rp-leaf">Support</h4>
            <ul className="mt-4 space-y-2 text-sm">
              <li><Link to="/faq" className="hover:text-rp-leaf">FAQ</Link></li>
              <li><Link to="/contact" className="hover:text-rp-leaf">Shipping</Link></li>
              <li><Link to="/contact" className="hover:text-rp-leaf">Returns</Link></li>
              <li><Link to="/contact" className="hover:text-rp-leaf">Contact</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div className="lg:col-span-3">
            <h4 className="font-mono text-xs font-bold uppercase tracking-[0.08em] text-rp-leaf">Company</h4>
            <ul className="mt-4 space-y-2 text-sm">
              <li><Link to="/" className="hover:text-rp-leaf">About</Link></li>
              <li>
                <Link to="/about" className="hover:text-rp-leaf">
                  About AURA
                </Link>
              </li>
              <li><Link to="/contact" className="hover:text-rp-leaf">Compliance</Link></li>
              <li><Link to="/contact" className="hover:text-rp-leaf">Careers</Link></li>
              <li><Link to="/" className="hover:text-rp-leaf">Research-use statement</Link></li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-rp-forest-2 pt-8 text-xs text-rp-leaf md:flex-row">
          <p>© {new Date().getFullYear()} AURA. All rights reserved.</p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <div className="flex items-center gap-2" aria-label="Accepted payment methods">
              <span className="flex h-6 items-center gap-1 rounded bg-rp-paper px-1.5 text-[10px] font-bold text-rp-forest">
                <CreditCard size={12} /> VISA
              </span>
              <span className="flex h-6 items-center gap-1 rounded bg-rp-paper px-1.5 text-[10px] font-bold text-rp-forest">
                MC
              </span>
              <span className="flex h-6 items-center gap-1 rounded bg-rp-paper px-1.5 text-[10px] font-bold text-rp-forest">
                AMEX
              </span>
              <span className="flex h-6 items-center gap-1 rounded bg-rp-paper px-1.5 text-[10px] font-bold text-rp-forest">
                PayPal
              </span>
            </div>
            <div className="flex gap-6">
              <Link to="/" className="hover:text-rp-paper">Terms</Link>
              <Link to="/" className="hover:text-rp-paper">Privacy</Link>
              <Link to="/" className="hover:text-rp-paper">Cookies</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
